import * as React from 'react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppUser } from 'Src/views/App/App';
import axios from 'Src/helpers/api/axiosInstance';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { Card } from './OverviewContent';
import './SltInsights.scss';
import './SltOverview.scss';
import './MyAccess.scss';

interface AccessProfile {
  school: string;
  role: string;
  scope: { kind: 'SCHOOL'; years: string[] };
  since: string | null;
  lastSignIn: string | null;
  studentDetails: boolean;
  canEditThresholds: boolean;
  canMessage: boolean;
  minGroupSize: number;
  traitFields: string[];
  administrators: Array<{ id: string; name: string; role: string }>;
}
const date = (value: string) =>
  value
    ? new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : 'Not recorded';
const login = (value: string) => {
  if (!value) return 'Not recorded';
  const at = new Date(value),
    today = new Date();
  return at.toDateString() === today.toDateString()
    ? `Today, ${at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
    : date(value);
};
export function MyAccess({ user }: { user: AppUser }) {
  const [data, setData] = useState<AccessProfile>(null),
    [error, setError] = useState(''),
    [retry, setRetry] = useState(0);
  const [administrator, setAdministrator] = useState<AccessProfile['administrators'][number]>(null);
  useEffect(() => {
    let active = true;
    setData(null);
    setError('');
    setAdministrator(null);
    axios
      .get(`${window.apiBase}/i/schools/${user.activeSchoolId}/slt/access-profile`, {
        headers: { usid: user.roleSessionKey }
      })
      .then(response => {
        if (active) setData(response.data);
      })
      .catch(() => {
        if (active) setError('Your access could not be loaded. Ask a school administrator to check your SLT role.');
      });
    return () => {
      active = false;
    };
  }, [user.activeSchoolId, user.roleSessionKey, retry]);
  const chip = (label: string, tone = 'fl') => <span className={`sla-chip ${tone}`}>{label}</span>;
  const rows = data
    ? [
        ['Overview, Clubs, Sport', chip('Full', 'up'), 'All year groups'],
        [
          'Student lists',
          chip(data.studentDetails ? 'Full' : 'Counts only', data.studentDetails ? 'up' : 'fl'),
          data.studentDetails
            ? 'Names visible with your student detail permission'
            : 'Student names and individual records are hidden'
        ],
        ...(data.traitFields.some(field =>
          ['pupilsPremium', 'freeSchoolMeals', 'specialEducationNeeds'].includes(field)
        )
          ? [
              [
                'Characteristics (PP, FSM, SEND)',
                chip(data.studentDetails ? 'Full' : 'Counts only', data.studentDetails ? 'up' : 'fl'),
                `Only school-tracked characteristics · groups under ${data.minGroupSize} are hidden`
              ]
            ]
          : []),
        ['Message tutors and parents', chip('View only'), 'Connect General messages to send messages'],
        ['Reports and exports', chip('Full', 'up'), 'Small groups hidden in every export'],
        [
          'Thresholds & alerts',
          chip(data.canEditThresholds ? 'Can edit' : 'View only', data.canEditThresholds ? 'up' : 'fl'),
          `Changes apply for everyone at ${data.school || user.activeSchoolName}`
        ],
        [
          'Events, bookings, student profiles',
          chip('Not in SLT'),
          <span>
            Edit these in the <Link to="/profile">main Squad In Touch app →</Link>
          </span>
        ]
      ]
    : [];
  return (
    <div className="slt-insights slt-access">
      <header className="sli-heading">
        <div>
          <h1>My access</h1>
          <p>What you can see in SLT Insights and who manages it</p>
        </div>
      </header>
      {error ? (
        <Card title="Unable to load access" subtitle="">
          <p role="alert">{error}</p>
          <button className="sli-button" onClick={() => setRetry(n => n + 1)}>
            Try again
          </button>
        </Card>
      ) : !data ? (
        <p role="status">Loading your access…</p>
      ) : (
        <>
          <div className="sla-layout">
            <Card title="What you can see" subtitle="Based on your role · read-only" body={false}>
              <div className="sla-tableWrap">
                <table className="sla-table">
                  <thead>
                    <tr>
                      <th>Area</th>
                      <th>Access</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr key={index}>
                        <td>{row[0]}</td>
                        <td>{row[1]}</td>
                        <td>{row[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
            <div className="sla-stack">
              <Card title="Your role" subtitle="" body={false}>
                <dl className="sla-role">
                  {[
                    ['Role', data.role],
                    ['Scope', 'Whole school'],
                    ['School', data.school || user.activeSchoolName],
                    ['Since', date(data.since)],
                    ['Last sign-in', login(data.lastSignIn)]
                  ].map(([label, value]) => (
                    <div key={label}>
                      <dt>{label}</dt>
                      <dd>{value}</dd>
                    </div>
                  ))}
                </dl>
              </Card>
              <Card title="Who manages this" subtitle="Ask them to change your access" body={false}>
                {data.administrators.length ? (
                  data.administrators.map(admin => (
                    <div className="sla-admin" key={admin.id}>
                      <div>
                        <strong>{admin.name}</strong>
                        <small>{admin.role}</small>
                      </div>
                      <button className="sli-button" onClick={() => setAdministrator(admin)}>
                        Message
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="sla-empty">
                    No administrator found ·{' '}
                    <a href="mailto:support@squadintouch.com?subject=SLT%20access%20request">
                      Contact Squad In Touch support
                    </a>
                  </p>
                )}
              </Card>
            </div>
          </div>
          <Card title="How your data is protected" subtitle="">
            <div className="sla-limits">
              <div>
                <h3>Small groups hidden</h3>
                <p>
                  Any group under {data.minGroupSize} students shows as &lt;{data.minGroupSize}.
                </p>
              </div>
              <div>
                <h3>Read-only</h3>
                <p>Nothing here changes registers, events or student records.</p>
              </div>
              <div>
                <h3>Actions and audit</h3>
                <p>
                  Reviewed alerts appear in Reports › <Link to="/slt/reports">Review log</Link>. Student detail access
                  is audited. Messaging requires General messages.
                </p>
              </div>
            </div>
          </Card>
        </>
      )}
      <SimpleModal
        isOpen={!!administrator}
        title="Connect General messages"
        onCloseClick={() => setAdministrator(null)}
        showFooter={false}
        customClass="sli-drawer sli-clubDrawer"
      >
        {administrator && (
          <div className="sla-message">
            <p>Connect the General messages module to send an access request to {administrator.name}.</p>
            <label>
              Access request
              <textarea
                readOnly
                value={`Access request: Please review my SLT Insights access at ${data?.school ||
                  user.activeSchoolName}.`}
              />
            </label>
            <p>No message has been sent.</p>
            <a
              className="sli-button sli-primary"
              href="mailto:support@squadintouch.com?subject=Connect%20General%20messages"
            >
              Contact support
            </a>
          </div>
        )}
      </SimpleModal>
    </div>
  );
}
